/**
 * Choosing a programme, then subjects.
 *
 * Two steps, because they are two decisions and the second depends on the first.
 * The account already exists by this point — this is not a sign-up wizard, it is
 * the one thing the rest of the app cannot work without.
 *
 * A school student sees their school's programmes and only those: the school's
 * curriculum list is exactly what the picker offers, and nothing else.
 */

import { useMemo, useState } from 'react'
import { Button, Card, Chip, EmptyState, ErrorState, SkeletonCard, subjectColour } from '@modulus/ui'
import { GraduationCap } from 'lucide-react'
import type { Curriculum } from '@modulus/api'
import { api } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useResource } from '../../lib/useResource'

export function Onboarding() {
  const { profile, school, reload, signOut } = useSession()
  const [step, setStep] = useState<'programme' | 'subjects'>('programme')
  const [chosen, setChosen] = useState<Curriculum | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const curriculums = useResource<Curriculum[]>(
    (signal) => api.learner.curriculums(signal),
    [],
    { snapshot: 'curriculums', userId: profile?.id ?? null },
  )

  const options = useMemo(() => curriculums.data ?? [], [curriculums.data])

  const pickProgramme = async (curriculum: Curriculum) => {
    setError(null)
    setBusy(true)
    try {
      await api.learner.selectCurriculum(curriculum.id)
      // `select` acknowledges and returns nothing else, so step 2 reads the subjects
      // off the copy the list already gave us rather than off the response.
      setChosen(curriculum)
      // Pre-select whatever the programme already marks as selected, so a student
      // whose school set it up has nothing to do here.
      setSelected(new Set(curriculum.subjects.filter((s) => s.is_selected).map((s) => s.id)))
      setStep('subjects')
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not select that programme.')
    } finally {
      setBusy(false)
    }
  }

  const finish = async () => {
    if (!chosen) return
    setError(null)
    setBusy(true)
    try {
      // Only the subjects whose state actually changed.
      const changes = chosen.subjects.filter((s) => s.is_selected !== selected.has(s.id))
      for (const subject of changes) {
        await api.learner.setSubjectSelection(subject.id, selected.has(subject.id))
      }
      reload()
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save your subjects.')
      setBusy(false)
    }
  }

  return (
    <div style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: 'var(--space-m)' }}>
      <div style={{ width: 'min(640px, 100%)' }} className="mx-stack">
        <div>
          <span className="mx-label">
            {step === 'programme' ? 'Step 1 of 2' : 'Step 2 of 2'}
          </span>
          <h1 className="mx-page-title" style={{ marginTop: 6 }}>
            {step === 'programme' ? 'Which programme are you taking?' : 'Which subjects?'}
          </h1>
          <p className="mx-page-subtitle">
            {step === 'programme'
              ? school
                ? `${school.name} runs the programmes below.`
                : 'This sets the syllabus your questions are written against.'
              : 'Pick the ones you study. You can change this any time.'}
          </p>
        </div>

        {step === 'programme' && (
          <>
            {curriculums.error && <ErrorState error={curriculums.error} onRetry={curriculums.refresh} />}
            {curriculums.loading && !curriculums.data && <SkeletonCard lines={2} />}

            {curriculums.data && options.length === 0 && (
              <EmptyState
                icon={<GraduationCap size={22} />}
                title="No programmes available"
                body={
                  school
                    ? `${school.name} hasn't added any programmes yet. Your teacher can add them from the school dashboard.`
                    : 'Something went wrong loading the programme list. Try again in a moment.'
                }
              />
            )}

            <div className="mx-stack" style={{ gap: 'var(--space-xs)' }}>
              {options.map((curriculum) => (
                <Card
                  key={curriculum.id}
                  interactive
                  role="button"
                  tabIndex={0}
                  aria-disabled={busy}
                  onClick={() => !busy && void pickProgramme(curriculum)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      if (!busy) void pickProgramme(curriculum)
                    }
                  }}
                  style={{ cursor: busy ? 'progress' : 'pointer' }}
                >
                  <p style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 600 }}>
                    {curriculum.name}
                  </p>
                  {curriculum.description && (
                    <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: 'var(--text-sm)' }}>
                      {curriculum.description}
                    </p>
                  )}
                  <p className="mx-label" style={{ marginTop: 8 }}>
                    {curriculum.subjects.length} subjects
                  </p>
                </Card>
              ))}
            </div>
          </>
        )}

        {step === 'subjects' && chosen && (
          <>
            <Card>
              <div className="mx-row">
                {chosen.subjects.map((subject) => (
                  <Chip
                    key={subject.id}
                    selected={selected.has(subject.id)}
                    onToggle={() =>
                      setSelected((current) => {
                        const next = new Set(current)
                        if (next.has(subject.id)) next.delete(subject.id)
                        else next.add(subject.id)
                        return next
                      })
                    }
                  >
                    <i
                      aria-hidden="true"
                      style={{
                        width: 3,
                        height: 14,
                        borderRadius: 2,
                        background: subjectColour(subject.id, subject.color_hex),
                      }}
                    />
                    {subject.name}
                  </Chip>
                ))}
              </div>
            </Card>

            <div className="mx-row">
              <Button size="lg" onClick={() => void finish()} busy={busy} disabled={selected.size === 0}>
                {selected.size === 0 ? 'Pick at least one' : `Start with ${selected.size} subjects`}
              </Button>
              <Button variant="ghost" onClick={() => setStep('programme')} disabled={busy}>
                Back
              </Button>
            </div>
          </>
        )}

        {error && (
          <p className="mx-field-error" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          className="mx-btn mx-btn-ghost mx-btn-sm"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => void signOut()}
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
